import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { CreditCard, Lock, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API_URL } from '../utils/api';
import { useCart } from '../context/CartContext';
import { getErrorMessage } from '../utils/errors';
import { formatPrice } from '../utils/format';
import { useDocumentTitle } from '../hooks/use-document-title';

// Validate an MM/YY expiration that isn't in the past.
const validateExpiration = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 4) return 'Enter the expiration date as MM/YY.';
  const month = Number(digits.slice(0, 2));
  const year = 2000 + Number(digits.slice(2));
  if (month < 1 || month > 12) return 'Enter a valid month (01–12).';
  const now = new Date();
  const endOfMonth = new Date(year, month, 1);
  if (endOfMonth <= new Date(now.getFullYear(), now.getMonth(), 1)) return 'This card has expired.';
  return '';
};

// Brand styles: pill buttons and inputs with a thin logo-gradient outline on focus.
const PILL = 'h-11 rounded-full px-5 text-sm font-bold';
const PILL_INK = `${PILL} bg-ink text-paper hover:bg-ink/90`;
const PILL_OUTLINE = `${PILL} gradient-border text-ink shadow-none hover:[--gb-bg:#F2F0EB]`;
const FIELD_INPUT =
  'h-12 rounded-xl bg-white border-[#DCD6C8] px-3.5 text-base shadow-none focus-visible:ring-[3px] focus-visible:ring-pink/10 focus-visible:border-transparent focus-visible:[background:linear-gradient(#fff,#fff)_padding-box,linear-gradient(120deg,#EC008C,#0CBFF2)_border-box]';
// Wrapper around the Sola iFrames (the iframe itself is left untouched).
const IFRAME_FIELD =
  'mt-2 h-12 rounded-xl overflow-hidden border border-[#DCD6C8] bg-white px-2 focus-within:border-transparent focus-within:ring-[3px] focus-within:ring-pink/10 focus-within:[background:linear-gradient(#fff,#fff)_padding-box,linear-gradient(120deg,#EC008C,#0CBFF2)_border-box]';
const FIELD_LABEL = 'text-sm font-bold text-ink';

/**
 * Sola Payments iFields-based checkout.
 *
 * Flow:
 *   1. On mount we call `POST /orders/checkout` which creates a pending Order
 *      on the backend and returns `{order_id, total_amount, ifields_key,
 *      ifields_version, software_name, software_version}`.
 *   2. We inject the Cardknox/Sola iFields <script> (the CDN used by Sola),
 *      initialize it with `setAccount(publicKey, softwareName, version)` and
 *      mount two iframe fields (card number + CVV). Sensitive data never
 *      touches the React app or our backend — the CDN swaps PAN/CVV for
 *      Single-Use Tokens (SUTs) before the form is submitted.
 *   3. On submit we call `getTokens(onSuccess, onError)` (global from the
 *      iFields script), grab the two tokens out of the hidden inputs, and
 *      POST them to `/orders/{id}/pay` alongside expiration, name, and ZIP.
 *   4. If approved → redirect to /checkout/success. If declined → toast.
 */
export default function CheckoutPage() {
  useDocumentTitle('Secure checkout');
  const navigate = useNavigate();
  const { fetchCart, cart, cartTotal } = useCart();

  const [config, setConfig] = useState(null);           // server response
  const [initError, setInitError] = useState(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Non-sensitive fields we collect in regular inputs.
  const [exp, setExp] = useState('');
  const [zip, setZip] = useState('');
  const [name, setName] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [cardNumberError, setCardNumberError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const cardTokenRef = useRef(null);
  const cvvTokenRef = useRef(null);
  const formRef = useRef(null);

  // ── Step 1: create the order and fetch the iFields key ──────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.post(
          `${API_URL}/orders/checkout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;

        if (resp.data.skip_payment) {
          await fetchCart();
          navigate(resp.data.redirect_url || '/checkout/success?admin=true');
          return;
        }
        setConfig(resp.data);
      } catch (err) {
        if (cancelled) return;
        setInitError(getErrorMessage(err, 'Could not start checkout. Please try again.'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchCart, navigate]);

  // ── Step 2: inject Sola iFields script and initialize ───────────────────
  useEffect(() => {
    if (!config?.ifields_key || !config?.ifields_version) return;

    const scriptId = 'sola-ifields-script';
    const existing = document.getElementById(scriptId);

    const init = () => {
      try {
        if (typeof window.setAccount !== 'function') {
          setInitError('Payment form failed to load. Please refresh and try again.');
          return;
        }
        window.setAccount(
          config.ifields_key,
          config.software_name || 'azamein',
          config.software_version || '1.0.0'
        );
        if (typeof window.enableAutoFormatting === 'function') {
          window.enableAutoFormatting(' ');
        }
        if (typeof window.addIfieldKeyPressCallback === 'function') {
          window.addIfieldKeyPressCallback((data) => {
            if (data.lastIfieldChanged === 'card-number') {
              if (data.cardNumberFormattedLength > 0 && !data.cardNumberIsValid) {
                setCardNumberError('Card number appears invalid');
              } else {
                setCardNumberError('');
              }
            }
          });
        }
        setScriptReady(true);
      } catch (e) {
        setInitError('Could not initialize the payment form.');
      }
    };

    if (existing) {
      if (window.setAccount) init();
      else existing.addEventListener('load', init, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://cdn.cardknox.com/ifields/${config.ifields_version}/ifields.min.js`;
    script.async = true;
    script.onload = init;
    script.onerror = () =>
      setInitError('Unable to reach payment form CDN. Check your network and retry.');
    document.head.appendChild(script);
  }, [config]);

  // ── Step 3: submit — tokenize first, then POST SUTs to the backend ──────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!scriptReady || !config) return;

    const errors = {};
    if (!name.trim()) errors.name = 'Enter the name shown on your card.';
    const expError = validateExpiration(exp);
    if (expError) errors.exp = expError;
    if (!/^\d{5}(-?\d{4})?$/.test(zip.trim())) errors.zip = 'Enter a valid 5-digit ZIP code.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please check the highlighted fields.');
      const firstField = { name: 'card-name', exp: 'card-exp', zip: 'card-zip' }[Object.keys(errors)[0]];
      document.getElementById(firstField)?.focus();
      return;
    }

    setSubmitting(true);
    const finish = async () => {
      const cardNumSut = cardTokenRef.current?.value?.trim() || '';
      const cvvSut = cvvTokenRef.current?.value?.trim() || '';
      if (!cardNumSut) {
        toast.error('Please enter your card number.');
        setSubmitting(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const resp = await axios.post(
          `${API_URL}/orders/${config.order_id}/pay`,
          {
            card_num_sut: cardNumSut,
            cvv_sut: cvvSut,
            exp: exp.replace(/\D/g, ''),
            name: name.trim(),
            zip: zip.trim(),
            save_card: saveCard,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (resp.data.approved) {
          await fetchCart();
          toast.success('Payment approved!');
          navigate(
            resp.data.redirect_url ||
              `/checkout/success?order_id=${config.order_id}`
          );
          return;
        }

        if (resp.data.requires_3ds) {
          toast.error(
            'This card requires 3D Secure. 3DS is not yet enabled — please try a different card.'
          );
          setSubmitting(false);
          return;
        }

        toast.error(resp.data.decline_reason || 'Payment was declined.');
        setSubmitting(false);
      } catch (err) {
        toast.error(getErrorMessage(err, 'Payment could not be processed. Please try again.'));
        if (err.response?.status === 409) {
          // The total changed (a design was switched): show the new amount before a retry.
          try {
            const status = await axios.get(`${API_URL}/checkout/status/${config.order_id}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setConfig((c) => ({ ...c, total_amount: status.data.total_amount }));
          } catch (e) {
            // Keep the old total; the server refuses a mismatched charge anyway.
          }
        }
        setSubmitting(false);
      }
    };

    // getTokens() populates the hidden inputs with SUTs, then fires onSuccess.
    if (typeof window.getTokens === 'function') {
      window.getTokens(
        () => finish(),
        () => {
          toast.error('Could not secure your card details. Please re-enter and try again.');
          setSubmitting(false);
        },
        30000
      );
    } else {
      toast.error('Payment form not ready. Please wait a moment and retry.');
      setSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (initError) {
    return (
      <div className="min-h-screen bg-paper">
        <Navbar />
        <main id="main-content" className="pt-32 pb-20 px-4 sm:px-6 max-w-xl mx-auto">
          <div className="gradient-border rounded-[28px] p-7 sm:p-10 text-center" role="alert">
            <h1 className="font-serif text-3xl font-semibold text-ink mb-2">Checkout unavailable</h1>
            <p className="text-[#4A4640] mb-7">{initError}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => navigate('/cart')} className={PILL_INK}>
                Back to cart
              </Button>
              <Button variant="ghost" onClick={() => window.location.reload()} className={PILL_OUTLINE}>
                Try again
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-paper">
        <Navbar />
        <main id="main-content" className="pt-40 pb-20 px-4 flex flex-col items-center gap-4">
          <Spinner label="Preparing secure checkout" />
          <p className="text-[#4A4640]">Preparing your secure checkout…</p>
        </main>
      </div>
    );
  }

  const iframeSrc = `https://cdn.cardknox.com/ifields/${config.ifields_version}/ifield.htm`;

  return (
    <div className="min-h-screen bg-paper">
      <Navbar />
      <main id="main-content" className="pt-28 sm:pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto mb-8">
          <Link
            to="/cart"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A4640] hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to cart
          </Link>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-ink mt-4">
            Secure <span className="italic font-medium">checkout</span>
          </h1>
        </div>
        <div className="max-w-5xl mx-auto grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left — Card form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 order-2 lg:order-1"
          >
            <div className="bg-white border border-[#E6E1D6] rounded-[24px] p-5 sm:p-8">
              <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-ink mb-6">
                <CreditCard className="h-5 w-5" aria-hidden="true" />
                Payment details
              </h2>
              <div>
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <div>
                    <Label htmlFor="card-name" className={FIELD_LABEL}>Cardholder Name</Label>
                    <Input
                      id="card-name"
                      type="text"
                      autoComplete="cc-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name on card"
                      required
                      className={`mt-2 ${FIELD_INPUT}`}
                      aria-invalid={Boolean(fieldErrors.name) || undefined}
                      aria-describedby={fieldErrors.name ? 'card-name-error' : undefined}
                      data-testid="card-name-input"
                    />
                    {fieldErrors.name && (
                      <p id="card-name-error" className="text-destructive text-sm mt-1">{fieldErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <Label id="card-number-label" className={FIELD_LABEL}>Card Number</Label>
                    <div
                      role="group"
                      aria-labelledby="card-number-label"
                      className={IFRAME_FIELD}
                      data-testid="card-number-frame"
                    >
                      <iframe
                        title="Card Number"
                        data-ifields-id="card-number"
                        data-ifields-placeholder="1234 5678 9012 3456"
                        src={iframeSrc}
                        style={{
                          border: 'none',
                          width: '100%',
                          height: '100%',
                        }}
                      />
                    </div>
                    {cardNumberError && (
                      <p className="text-destructive text-sm mt-1" role="alert">{cardNumberError}</p>
                    )}
                    {/* Hidden inputs the CDN populates with SUTs before submit. */}
                    <input
                      ref={cardTokenRef}
                      name="xCardNum"
                      type="hidden"
                      data-ifields-id="card-number-token"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="card-exp" className={FIELD_LABEL}>Expiration</Label>
                      <Input
                        id="card-exp"
                        type="text"
                        inputMode="numeric"
                        autoComplete="cc-exp"
                        maxLength={5}
                        value={exp}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setExp(raw.length > 2 ? `${raw.slice(0, 2)}/${raw.slice(2)}` : raw);
                        }}
                        placeholder="MM/YY"
                        required
                        className={`mt-2 ${FIELD_INPUT}`}
                        aria-invalid={Boolean(fieldErrors.exp) || undefined}
                        aria-describedby={fieldErrors.exp ? 'card-exp-error' : undefined}
                        data-testid="card-exp-input"
                      />
                      {fieldErrors.exp && (
                        <p id="card-exp-error" className="text-destructive text-sm mt-1">{fieldErrors.exp}</p>
                      )}
                    </div>
                    <div>
                      <Label id="cvv-label" className={FIELD_LABEL}>CVV</Label>
                      <div
                        role="group"
                        aria-labelledby="cvv-label"
                        className={IFRAME_FIELD}
                        data-testid="cvv-frame"
                      >
                        <iframe
                          title="CVV"
                          data-ifields-id="cvv"
                          data-ifields-placeholder="CVV"
                          src={iframeSrc}
                          style={{
                            border: 'none',
                            width: '100%',
                            height: '100%',
                          }}
                        />
                      </div>
                      <input
                        ref={cvvTokenRef}
                        name="xCVV"
                        type="hidden"
                        data-ifields-id="cvv-token"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="card-zip" className={FIELD_LABEL}>Billing ZIP</Label>
                    <Input
                      id="card-zip"
                      type="text"
                      inputMode="numeric"
                      autoComplete="postal-code"
                      maxLength={10}
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      placeholder="10001"
                      required
                      className={`mt-2 ${FIELD_INPUT}`}
                      aria-invalid={Boolean(fieldErrors.zip) || undefined}
                      aria-describedby={fieldErrors.zip ? 'card-zip-error' : undefined}
                      data-testid="card-zip-input"
                    />
                    {fieldErrors.zip && (
                      <p id="card-zip-error" className="text-destructive text-sm mt-1">{fieldErrors.zip}</p>
                    )}
                  </div>

                  <label className="flex items-center gap-2.5 text-sm text-[#4A4640] select-none cursor-pointer">
                    <input
                      className="h-4 w-4 accent-[#1A1A1A]"
                      type="checkbox"
                      checked={saveCard}
                      onChange={(e) => setSaveCard(e.target.checked)}
                      data-testid="save-card-toggle"
                    />
                    Save this card for future purchases
                  </label>

                  <label
                    data-ifields-id="card-data-error"
                    className="block text-sm text-destructive"
                    aria-live="polite"
                  />

                  <Button
                    type="submit"
                    disabled={!scriptReady || submitting}
                    className={`${PILL_INK} w-full h-12 text-[15px] mt-2`}
                    data-testid="pay-btn"
                  >
                    <Lock className="h-4 w-4" aria-hidden="true" />
                    {submitting
                      ? 'Processing payment…'
                      : !scriptReady
                      ? 'Loading secure form…'
                      : `Pay ${formatPrice(config.total_amount ?? cartTotal)}`}
                  </Button>

                  <p className="text-xs text-[#6B665C] flex items-center gap-1.5 justify-center mt-2 text-center">
                    <ShieldCheck className="h-3 w-3 shrink-0" aria-hidden="true" />
                    Card data is tokenized by Sola Payments. We never see your card number.
                  </p>
                </form>
              </div>
            </div>
          </motion.div>

          {/* Right — Summary (shown first on mobile) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="order-1 lg:order-2 self-start w-full"
          >
            <div className="gradient-border gb-cream rounded-[24px] p-6 flex flex-col gap-3 lg:sticky lg:top-28">
              <h2 className="font-serif text-2xl font-semibold text-ink mb-1">Order summary</h2>
              {cart.items?.map((item) => (
                <div key={item.id} className="flex justify-between gap-3 text-[15px]">
                  <span className="text-[#4A4640] truncate">
                    {item.invitation?.name || 'Invitation'}
                  </span>
                  <span className="font-semibold shrink-0">{formatPrice(item.price || 0)}</span>
                </div>
              ))}
              <div className="border-t border-[#DCD6C8] pt-4 mt-1 flex justify-between items-baseline">
                <span className="font-bold">Total</span>
                <span className="font-serif text-3xl font-semibold text-ink">
                  {formatPrice(config.total_amount ?? cartTotal)}
                </span>
              </div>
              <p className="text-xs text-[#6B665C] flex items-center gap-1.5 pt-1">
                <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Encrypted payment by Sola Payments
              </p>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
